import { useEffect, useState } from "react";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, formatApiError, formatDate, Kicker, PrimaryButton, Spinner, StatusPill } from "@/components/employer/portal/shared";

const STEPS = ["pending", "under_review", "verified"];
const LABELS = { pending: "Pending", under_review: "Under Review", verified: "Verified" };

export default function EmployerVerification() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        const res = await apiFetch("/employer/verification");
        if (res.ok) setData(await res.json());
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async () => {
        if (busy) return;
        setBusy(true);
        setError("");
        const res = await apiFetch("/employer/verification/submit", { method: "POST" });
        if (res.ok) {
            await load();
        } else {
            const body = await res.json().catch(() => ({}));
            setError(formatApiError(body.detail, "Verification could not be submitted yet."));
        }
        setBusy(false);
    };

    const activeIndex = data ? Math.max(0, STEPS.indexOf(data.status)) : 0;

    return (
        <EmployerLayout title="Verification" subtitle="Afrinexa verifies every employer before vacancies can be submitted for public review.">
            {!data ? <Spinner /> : (
                <div className="grid gap-8 lg:grid-cols-[0.7fr_0.3fr]" data-testid="employer-verification-page">
                    <section className="border border-ink/10 bg-ivory p-7 sm:p-9">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <Kicker>Verification status</Kicker>
                            <StatusPill status={data.status} type="verification" />
                        </div>

                        <div className="mt-10 grid gap-6 sm:grid-cols-3">
                            {STEPS.map((step, index) => {
                                const active = index <= activeIndex || data.status === "verified";
                                return (
                                    <div key={step} data-testid={`verification-step-${step}`} className="relative border-t border-ink/10 pt-5">
                                        <span className={`absolute -top-[5px] left-0 h-2.5 w-2.5 rotate-45 ${active ? "bg-champagne" : "bg-ink/15"}`} />
                                        <p className={`font-serif text-xl font-medium ${active ? "text-ink" : "text-ink/35"}`}>{LABELS[step]}</p>
                                        <p className="mt-2 text-xs leading-relaxed text-ink/45">
                                            {step === "pending" && "Account created and profile being prepared."}
                                            {step === "under_review" && "Afrinexa is reviewing your company details."}
                                            {step === "verified" && "Approved to submit vacancies for publication review."}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {(data.status === "rejected" || data.status === "suspended") && (
                            <div data-testid="verification-attention" className="mt-9 border-l-2 border-champagne bg-sand p-5">
                                <p className="text-sm font-medium text-ink">{data.status === "rejected" ? "Verification needs attention" : "Account suspended"}</p>
                                <p className="mt-2 text-sm leading-relaxed text-ink/60">{data.notes || "Please contact Afrinexa Global for more information."}</p>
                            </div>
                        )}

                        {data.notes && data.status !== "rejected" && data.status !== "suspended" && (
                            <div className="mt-9 border-l-2 border-champagne bg-sand p-5">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Review note</p>
                                <p className="mt-2 text-sm leading-relaxed text-ink/70">{data.notes}</p>
                            </div>
                        )}

                        <div className="mt-10 grid gap-5 border-t border-ink/10 pt-7 sm:grid-cols-2">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Profile completion</p>
                                <p className="mt-2 font-serif text-4xl font-medium">{data.completion}%</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Submitted</p>
                                <p className="mt-2 text-sm text-ink/70">{formatDate(data.submitted_at, true)}</p>
                                <p className="mt-1 text-sm text-ink/70">Reviewed: {formatDate(data.reviewed_at, true)}</p>
                            </div>
                        </div>

                        {error && <p data-testid="verification-error" className="mt-6 border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}
                        {data.status !== "verified" && data.status !== "under_review" && data.status !== "suspended" && (
                            <PrimaryButton data-testid="verification-submit" busy={busy} onClick={submit} className="mt-8">
                                Submit for Verification
                            </PrimaryButton>
                        )}
                    </section>

                    <aside className="h-fit border border-ink/10 bg-night p-7 text-ivory">
                        <ShieldCheck size={24} strokeWidth={1.4} className="text-champagne" />
                        <h2 className="mt-5 font-serif text-2xl font-medium tracking-tight">What Afrinexa checks</h2>
                        <ul className="mt-5 space-y-4 text-sm leading-relaxed text-ivory/60">
                            <li className="flex gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-champagne" /> Company identity and registration details</li>
                            <li className="flex gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-champagne" /> Business and recruitment contact information</li>
                            <li className="flex gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-champagne" /> Supporting company documents where required</li>
                        </ul>
                        <a href="/employer/documents" data-testid="verification-documents-link" className="mt-7 inline-flex border border-ivory/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:border-champagne hover:text-champagne">
                            Upload documents
                        </a>
                    </aside>
                </div>
            )}
        </EmployerLayout>
    );
}
